import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface ForgotPasswordFormProps {
  onSuccess: () => void;
  onBack: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSuccess, onBack }) => {
  const [step, setStep] = useState<'email' | 'verify' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userId, setUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Countdown timer for verification code
  useEffect(() => {
    if (step === 'verify' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [step, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiService.forgotPassword(email);
      setUserId(response.userId);
      setStep('verify');
      setTimeLeft(600); // Reset timer
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || !userId) return;
    
    setStep('reset');
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiService.resetPassword(userId, code, newPassword);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError('');

    try {
      await apiService.forgotPassword(email);
      setTimeLeft(600); // Reset timer
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const PasswordToggleButton = ({ show, onClick }: { show: boolean; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '1rem',
        color: '#666',
        padding: '4px'
      }}
    >
      {show ? '👁️' : '👁️‍🗨️'}
    </button>
  );

  return (
    <div className="auth-form">
      {step === 'email' && (
        <>
          <h2>Forgot Password</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem', textAlign: 'center' }}>
            Enter your email address and we'll send you a verification code to reset your password.
          </p>
          
          <form onSubmit={handleEmailSubmit}>
            <div className="form-group">
              <label htmlFor="reset-email">Email Address:</label>
              <input
                type="email"
                id="reset-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your email"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading} style={{ marginBottom: '1rem' }}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </form>

          <button type="button" className="link-button" onClick={onBack}>
            Back to Login
          </button>
        </>
      )}

      {step === 'verify' && (
        <>
          <h2>Enter Verification Code</h2>
          <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📧</div>
            <p style={{ color: '#666', marginBottom: '0.5rem' }}>
              We sent a 6-digit code to:
            </p>
            <p style={{ fontWeight: 'bold', color: '#333' }}>{email}</p>
          </div>

          <form onSubmit={handleCodeSubmit}>
            <div className="form-group">
              <label htmlFor="reset-code">Verification Code:</label>
              <input
                type="text"
                id="reset-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
                style={{
                  textAlign: 'center',
                  fontSize: '1.2rem',
                  letterSpacing: '0.2rem',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem', textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
              {timeLeft > 0 ? (
                <span>Code expires in: <strong>{formatTime(timeLeft)}</strong></span>
              ) : (
                <span style={{ color: '#f44336' }}>Code has expired</span>
              )}
            </div>

            <button type="submit" disabled={code.length !== 6 || timeLeft === 0} style={{ marginBottom: '1rem' }}>
              Verify Code
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              className="link-button"
              onClick={handleResendCode}
              disabled={loading || timeLeft > 540} // Allow resend only after 1 minute
              style={{ marginRight: '1rem' }}
            >
              {loading ? 'Sending...' : 'Resend Code'}
            </button>

            <button type="button" className="link-button" onClick={onBack}>
              Back to Login
            </button>
          </div>
        </>
      )}

      {step === 'reset' && (
        <>
          <h2>Reset Password</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem', textAlign: 'center' }}>
            Enter your new password below.
          </p>

          <form onSubmit={handleResetSubmit}>
            <div className="form-group">
              <label htmlFor="new-password">New Password:</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                />
                <PasswordToggleButton 
                  show={showPassword} 
                  onClick={() => setShowPassword(!showPassword)} 
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirm-new-password">Confirm New Password:</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirm-new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                />
                <PasswordToggleButton 
                  show={showConfirmPassword} 
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                />
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading} style={{ marginBottom: '1rem' }}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <button type="button" className="link-button" onClick={onBack}>
            Back to Login
          </button>
        </>
      )}
    </div>
  );
};

export default ForgotPasswordForm;